import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    Tabs,
    Tab,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell
} from '@mui/material';
import {
    People as PeopleIcon,
    PhotoLibrary as PhotoIcon,
    Settings as SettingsIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { fetchUsers, updateUserRole, deleteUser, createUser, syncAuthUsersWithDatabase } from '../../services/adminService';
import { UserRoles } from '../../services/authService';

const AdminDashboard = ({ user, userRole }) => {
    const [activeTab, setActiveTab] = useState(0);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const TabPanel = ({ children, value, index }) => (
        <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
            {value === index && children}
        </Box>
    );

    return (
        <Box sx={{ pt: 8, pb: 4 }}>
            <Container maxWidth="xl">
                <Grid container spacing={3}>
                    {/* Admin Header */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h4" gutterBottom>
                                Admin Dashboard
                            </Typography>
                            <Typography color="textSecondary">
                                Manage users, content, and system settings
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* Admin Navigation */}
                    <Grid item xs={12}>
                        <Paper sx={{ width: '100%' }}>
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                indicatorColor="primary"
                                textColor="primary"
                                variant="fullWidth"
                            >
                                <Tab icon={<PeopleIcon />} label="User Management" />
                                <Tab icon={<PhotoIcon />} label="Content Management" />
                                <Tab icon={<SettingsIcon />} label="System Settings" />
                            </Tabs>
                        </Paper>
                    </Grid>

                    {/* Tab Content */}
                    <Grid item xs={12}>
                        <TabPanel value={activeTab} index={0}>
                            <UserManagement userRole={userRole} />
                        </TabPanel>
                        <TabPanel value={activeTab} index={1}>
                            <ContentManagement />
                        </TabPanel>
                        <TabPanel value={activeTab} index={2}>
                            <SystemSettings />
                        </TabPanel>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

// User Management Tab Content
const UserManagement = ({ userRole }) => {
    const [users, setUsers] = useState({});
    const [editingUser, setEditingUser] = useState(null);
    const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
    const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
    const [newUserData, setNewUserData] = useState({
        email: '',
        password: '',
        role: 'user'
    });
    const [isSyncing, setIsSyncing] = useState(false);

    const loadUsers = async () => {
        try {
            setIsSyncing(true);
            // First sync Auth users with Database
            await syncAuthUsersWithDatabase(userRole);
            // Then fetch all users
            const fetchedUsers = await fetchUsers(userRole);
            setUsers(fetchedUsers);
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setIsSyncing(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, [userRole]);

    const handleCreateUser = async () => {
        try {
            await createUser(newUserData, userRole);
            await loadUsers();
            setNewUserDialogOpen(false);
            setNewUserData({ email: '', password: '', role: 'user' });
        } catch (error) {
            console.error('Error creating user:', error);
        }
    };

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">User Management</Typography>
                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={loadUsers}
                        disabled={isSyncing}
                        sx={{ mr: 1 }}
                    >
                        {isSyncing ? 'Syncing...' : 'Sync Users'}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setNewUserDialogOpen(true)}
                    >
                        Add New User
                    </Button>
                </Box>
            </Box>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Created At</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(users).map(([userId, user]) => (
                            <TableRow key={userId}>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>{user.role}</TableCell>
                                <TableCell>
                                    {new Date(user.created_at).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                    <IconButton 
                                        onClick={() => setEditingUser({ ...user, id: userId })}
                                        color="primary"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton 
                                        onClick={() => setDeleteConfirmUser({ ...user, id: userId })}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* New User Dialog */}
            <Dialog open={newUserDialogOpen} onClose={() => setNewUserDialogOpen(false)}>
                <DialogTitle>Add New User</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Email"
                        value={newUserData.email}
                        onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        value={newUserData.password}
                        onChange={(e) => setNewUserData(prev => ({ ...prev, password: e.target.value }))}
                        margin="normal"
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={newUserData.role}
                            onChange={(e) => setNewUserData(prev => ({ ...prev, role: e.target.value }))}
                        >
                            <MenuItem value="user">User</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewUserDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateUser} color="primary">
                        Create User
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Role Dialog */}
            <Dialog open={!!editingUser} onClose={() => setEditingUser(null)}>
                <DialogTitle>Edit User Role</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={users[editingUser]?.role || 'user'}
                            onChange={(e) => handleRoleChange(editingUser, e.target.value)}
                        >
                            <MenuItem value={UserRoles.USER}>User</MenuItem>
                            <MenuItem value={UserRoles.ADMIN}>Admin</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingUser(null)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteConfirmUser} onClose={() => setDeleteConfirmUser(null)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this user? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmUser(null)}>Cancel</Button>
                    <Button 
                        onClick={() => handleDeleteUser(deleteConfirmUser)}
                        color="error"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

// Content Management Tab Content
const ContentManagement = () => (
    <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
            Content Management
        </Typography>
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1">Albums</Typography>
                    {/* Album management content */}
                </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1">Photos</Typography>
                    {/* Photo management content */}
                </Paper>
            </Grid>
        </Grid>
    </Paper>
);

// System Settings Tab Content
const SystemSettings = () => (
    <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
            System Settings
        </Typography>
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1">General Settings</Typography>
                    {/* General settings content */}
                </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1">Security Settings</Typography>
                    {/* Security settings content */}
                </Paper>
            </Grid>
        </Grid>
    </Paper>
);

export default AdminDashboard; 